import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { loginSchema } from '../../schemas/auth';

// This component now serves as a Login Form
const LoginOrSignupForm = () => {
  const {
    loginWithEmail,
    isAuthenticated,
    isLoading,
    isLoggingIn,
    authError,
    clearAuthError,
    bruteForceStatus,
    isLoginBlocked,
    loginDelay,
    delayMessage,
    loginWithMicrosoft,
    isLinkingMode,
    linkingEmail,
    cancelLinking,
  } = useAuth();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showRefreshMessage, setShowRefreshMessage] = useState(false); // New state for inactivity message
  const [showEncryptionLogoutMessage, setShowEncryptionLogoutMessage] = useState(false);
  const [logoutReason, setLogoutReason] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const theme = useTheme();

  React.useEffect(() => {
    if (sessionStorage.getItem('loggedOutDueToInactivity') === 'true') {
      setShowRefreshMessage(true);
    }
    if (sessionStorage.getItem('loggedOutDueToStaleEncryption') === 'true') {
      setShowEncryptionLogoutMessage(true);
      sessionStorage.removeItem('loggedOutDueToStaleEncryption');
    }
    const storedLogoutReason = sessionStorage.getItem('logoutReason');
    if (storedLogoutReason) {
      setLogoutReason(storedLogoutReason);
      sessionStorage.removeItem('logoutReason');
    }
  }, []); // Run once on mount

  React.useEffect(() => {
    // Only clear auth errors when user is actively typing, not due to system states
    if (!isLoginBlocked && !delayMessage && loginDelay === 0) {
      clearAuthError();
    }
  }, [clearAuthError, isLoginBlocked, delayMessage, loginDelay]);

  React.useEffect(() => {
    // Only show snackbar for actual authentication errors, not brute force messages
    if (
      authError &&
      !delayMessage &&
      !isLoginBlocked &&
      loginDelay === 0 &&
      !authError.includes('wait') &&
      !authError.includes('locked') &&
      !authError.includes('attempts remaining')
    ) {
      showSnackbar(authError, 'error');
    }
  }, [authError, showSnackbar, delayMessage, isLoginBlocked, loginDelay]);

  const extractValidationErrors = (
    issues: { path: PropertyKey[]; message: string }[]
  ): { email?: string; password?: string } => {
    const errors: { email?: string; password?: string } = {};
    for (const issue of issues) {
      const field = issue.path[0];
      if (field === 'email') {
        errors.email = issue.message;
      } else if (field === 'password') {
        errors.password = issue.message;
      }
    }
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    clearAuthError();
    setValidationErrors({});

    const targetEmail = isLinkingMode ? linkingEmail : email.trim();
    if (/[<>]/.test(targetEmail)) {
      setValidationErrors({ email: 'Please enter a valid email address' });
      return;
    }

    const validationResult = loginSchema.safeParse({ email: targetEmail, password });
    if (!validationResult.success) {
      setValidationErrors(extractValidationErrors(validationResult.error.issues));
      return;
    }

    try {
      await loginWithEmail(targetEmail, password);
      // Navigation is handled automatically by AppContent based on auth state and isRecoveryNeeded
    } catch (_error) {
      // Auth error handled by context
    }
  };

  // Handle signup separately for now, can be integrated into one form with a toggle
  // const handleSignUp = async (event) => { // COMMENTED OUT THIS FUNCTION
  //     event.preventDefault();
  //     clearAuthError();
  //     await signUpWithEmail(email, password);
  // };

  // Use authContext isLoading for global load, formLoading for button disabling
  if (isLoading && !authError && !isAuthenticated) {
    return <p>Loading...</p>;
  }

  if (showRefreshMessage) {
    return (
      <Box sx={{ mt: 0, width: '100%', textAlign: 'center' }}>
        <Alert
          severity="error"
          sx={{
            backgroundColor: theme.palette.bidlogiq.rust,
            color: theme.palette.bidlogiq.white,
            mb: 2,
          }}
        >
          You were logged out due to inactivity.
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please refresh the page to log in again.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            sessionStorage.removeItem('loggedOutDueToInactivity');
            window.location.reload();
          }}
          sx={{
            fontSize: '1rem',
            backgroundColor: theme.palette.bidlogiq.brick,
            color: theme.palette.bidlogiq.white,
            '&:hover': {
              backgroundColor: theme.palette.bidlogiq.rust,
            },
          }}
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
      {showEncryptionLogoutMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Logged Out for Security
          </Typography>
          <Typography variant="body2">
            Your session has been reset. Please log in again to continue.
          </Typography>
        </Alert>
      )}

      {logoutReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {logoutReason}
        </Alert>
      )}

      {isLinkingMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          An account already exists for <b>{linkingEmail}</b>. Please enter your existing password
          to link your Microsoft account.
        </Alert>
      )}

      {!isLinkingMode && (
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!authError || !!validationErrors.email}
          helperText={validationErrors.email}
          sx={{
            backgroundColor: theme.palette.greyLight,
            color: theme.palette.text.primary,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.bidlogiq.black,
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.bidlogiq.orange,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.bidlogiq.orange,
                borderWidth: '2px',
              },
            },
          }}
        />
      )}
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type="password"
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={!!authError || !!validationErrors.password}
        helperText={validationErrors.password}
        sx={{
          backgroundColor: theme.palette.greyLight,
          color: theme.palette.text.primary,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: theme.palette.bidlogiq.black,
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: theme.palette.bidlogiq.orange,
              borderWidth: '2px',
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.bidlogiq.orange,
              borderWidth: '2px',
            },
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          onClick={() => navigate('/forgot-password')}
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            backgroundColor: 'transparent',
            color: theme.palette.secondary.dark,
            padding: 1,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              fontWeight: 600,
            },
          }}
        >
          Forgot Password?
        </Button>
      </Box>
      {/* Brute Force Protection Status */}
      {bruteForceStatus && !isLoginBlocked && bruteForceStatus.attemptsRemaining < 5 && (
        <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
          Security Notice: {bruteForceStatus.attemptsRemaining} login attempts remaining before
          temporary lockout.
        </Alert>
      )}

      {isLoginBlocked && (
        <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
          Account temporarily locked due to multiple failed login attempts. Please try again later.
        </Alert>
      )}

      {delayMessage && (
        <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
          {delayMessage}
        </Alert>
      )}

      {authError && !delayMessage && (
        <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
          {authError}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          backgroundColor: theme.palette.bidlogiq.brick,
          color: theme.palette.bidlogiq.white,
          mt: 3,
          mb: 2,
          '&:hover': {
            backgroundColor: theme.palette.bidlogiq.brick,
          },
          '&:disabled': {
            backgroundColor: theme.palette.greyLight,
            color: theme.palette.greyContrast,
          },
        }}
        disabled={isLoggingIn || isLoading || isLoginBlocked || loginDelay > 0}
      >
        {isLoggingIn || isLoading || loginDelay > 0 ? (
          <CircularProgress size={24} sx={{ color: theme.palette.bidlogiq.brick }} />
        ) : (
          <Typography
            sx={{ fontSize: '1.4rem', fontWeight: 600, color: theme.palette.bidlogiq.white }}
          >
            {isLoginBlocked
              ? 'Account Locked'
              : isLinkingMode
                ? 'Link Account & Sign In'
                : 'Sign In'}
          </Typography>
        )}
      </Button>

      {isLinkingMode && (
        <Button
          fullWidth
          variant="text"
          onClick={cancelLinking}
          disabled={isLoggingIn || isLoading}
          sx={{ mb: 2 }}
        >
          Cancel
        </Button>
      )}

      {!isLinkingMode && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
              OR
            </Typography>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={loginWithMicrosoft}
            disabled={isLoggingIn || isLoading || isLoginBlocked || loginDelay > 0}
            sx={{
              mb: 2,
              bgcolor: theme.palette.bidlogiq.brick,
              color: theme.palette.bidlogiq.white,
              py: 1.5,
              '&:hover': { bgcolor: theme.palette.bidlogiq.brick },
              '&:disabled': {
                backgroundColor: theme.palette.greyLight,
                color: theme.palette.greyContrast,
              },
            }}
          >
            {isLoggingIn || isLoading || loginDelay > 0 ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                Continue with Microsoft SSO
              </Typography>
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

export default LoginOrSignupForm;
